import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LOGO_URL } from "../constants/brand";

export default function TrustPortalSignupPage() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [referredBy, setReferredBy] = useState(null);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      api.get(`/trust-portal/lookup-referral?code=${encodeURIComponent(ref)}`)
        .then(({ data }) => { if (data.found) setReferredBy(data.display_name); })
        .catch(() => {});
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/trust-portal/signup", { ...form, referral_code: searchParams.get("ref") || "" });
      setSuccess(data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-auth-bg flex overflow-hidden" data-testid="trust-portal-signup-page">
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md" data-testid="trust-portal-signup-card">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="Iron Gate Consulting" className="w-24 h-24 object-contain mb-4 auth-glow" />
            <h1 className="font-serif text-2xl text-auth-cream tracking-tight text-center">Start Your Trust Portal Intake</h1>
            <p className="text-auth-muted text-sm mt-2 tracking-wide text-center">Personal call before you pay. Military legal review included.</p>
          </div>
          {success ? (
            <div className="text-center" data-testid="trust-portal-signup-success">
              <p className="text-auth-cream text-sm">{success.message}</p>
              <Link to="/trust-portal/login" data-testid="trust-portal-signup-login-link" className="text-auth-gold text-sm mt-6 inline-block hover:text-auth-goldHover">Log in now</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="trust-portal-signup-form">
              {referredBy && (
                <p className="text-center text-sm text-auth-gold bg-auth-gold/10 border border-auth-gold/25 py-2 px-3" data-testid="trust-portal-referred-by-banner">
                  Referred by: {referredBy}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="trust-portal-first-name-input" required placeholder="First name" title="Your first name." value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11" />
                <Input data-testid="trust-portal-last-name-input" required placeholder="Last name" title="Your last name." value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11" />
              </div>
              <Input data-testid="trust-portal-email-input" type="email" required placeholder="Email" title="The email you'll use to log in to your Trust Portal account." value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11" />
              <Input data-testid="trust-portal-phone-input" placeholder="Phone" title="Your phone number, so IGC can schedule your personal intake call." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11" />
              <Input data-testid="trust-portal-password-input" type="password" required placeholder="Choose a password" title="Pick a password you'll use to log back into your Trust Portal account." value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11" />
              {error && <p className="text-auth-danger text-sm" data-testid="trust-portal-signup-error">{error}</p>}
              <Button data-testid="trust-portal-signup-submit-button" type="submit" disabled={submitting} title="Creates your Trust Portal account so you can start your intake." className="w-full h-12 bg-auth-gold hover:bg-auth-goldHover text-black font-medium">
                {submitting ? "Creating account..." : "Create My Account"}
              </Button>
              <p className="text-center text-sm text-auth-muted mt-2">Already have an account? <Link to="/trust-portal/login" data-testid="trust-portal-signup-goto-login-link" title="Go to the Trust Portal login page." className="text-auth-gold hover:text-auth-goldHover">Log in</Link></p>
            </form>
          )}
        </div>
      </div>
      <div className="hidden lg:flex flex-1 relative items-center justify-center bg-auth-surface overflow-hidden">
        <div className="absolute inset-0 opacity-[0.14]" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #d9b44a 0, transparent 55%)" }} />
        <img src={LOGO_URL} alt="" className="w-[85%] max-w-2xl opacity-[0.22] select-none" style={{ filter: "brightness(0.7)" }} draggable="false" />
      </div>
    </div>
  );
}
